import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

/**
 * One search box that filters by property, AWS-console style. Focus it and it lists the
 * properties; pick one and it lists that property's values; type and it matches values across
 * every property, plus a free-text search. Applied filters show as tokens under the box.
 *
 * Semantics are fixed and stated in the UI: values of ONE property are OR'ed (`Region = US or
 * EU`), properties are AND'ed. The component owns no filter state — the page keeps it in the URL
 * and passes it back in through `properties[].selected` and `text`.
 */

export interface FilterOption {
  value: string;
  label: string;
  /** Shown on the right of the option, e.g. a row count. */
  count?: string;
}

export interface FilterProperty {
  key: string;
  label: string;
  options: FilterOption[];
  selected: string[];
}

interface Item {
  id: string;
  label: ReactNode;
  side?: string;
  checked?: boolean;
  run: () => void;
}

interface Group {
  title: string;
  items: Item[];
}

const MAX_VALUE_MATCHES = 8;

export function PropertyFilter({
  properties,
  onToggle,
  onClearProperty,
  text,
  onText,
  textHint,
  placeholder,
  leading,
  trailing,
  onClearAll,
}: {
  properties: FilterProperty[];
  onToggle: (key: string, value: string) => void;
  onClearProperty: (key: string) => void;
  /** The applied free-text search ('' = none). */
  text: string;
  onText: (t: string | null) => void;
  /** What free text matches, e.g. "UUID, serial or VPN IP". */
  textHint: string;
  placeholder: string;
  /** Controls placed before the box (the refresh button). */
  leading?: ReactNode;
  /** Controls placed after the box (counts, chips). */
  trailing?: ReactNode;
  onClearAll: () => void;
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [hl, setHl] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  // "Region = us" → property mode for Region with the term "us".
  const propMode = useMemo(() => {
    const m = /^(.+?)\s*=\s*(.*)$/.exec(input);
    if (!m) return null;
    const prop = properties.find((p) => p.label.toLowerCase() === m[1].trim().toLowerCase());
    return prop ? { prop, term: m[2].trim().toLowerCase() } : null;
  }, [input, properties]);

  // A filter was applied: empty the box and close the list (click the box to add another).
  const reset = () => {
    setInput('');
    setHl(-1);
    setOpen(false);
  };

  const pick = (p: FilterProperty, v: string) => {
    onToggle(p.key, v);
    reset();
  };

  const groups: Group[] = useMemo(() => {
    const valueItem = (p: FilterProperty, o: FilterOption, withLabel: boolean): Item => ({
      id: `${p.key}=${o.value}`,
      label: withLabel ? (
        <>
          <span className="muted">{p.label} =</span> {o.label}
        </>
      ) : (
        o.label
      ),
      side: o.count,
      checked: p.selected.includes(o.value),
      run: () => pick(p, o.value),
    });

    if (propMode) {
      const { prop, term } = propMode;
      const items = prop.options.filter((o) => o.label.toLowerCase().includes(term)).map((o) => valueItem(prop, o, false));
      return [{ title: `${prop.label} values`, items }];
    }

    const t = input.trim().toLowerCase();
    const out: Group[] = [];
    const props = properties.filter((p) => !t || p.label.toLowerCase().includes(t));
    if (props.length) {
      out.push({
        title: 'Properties',
        items: props.map((p) => ({
          id: `prop:${p.key}`,
          label: p.label,
          side: p.selected.length ? `${p.selected.length} selected` : undefined,
          run: () => {
            setInput(`${p.label} = `);
            setHl(-1);
            inputRef.current?.focus();
          },
        })),
      });
    }
    if (t) {
      const values = properties
        .flatMap((p) => p.options.filter((o) => o.label.toLowerCase().includes(t)).map((o) => valueItem(p, o, true)))
        .slice(0, MAX_VALUE_MATCHES);
      if (values.length) out.push({ title: 'Values', items: values });
      out.push({
        title: 'Free text',
        items: [
          {
            id: 'text',
            label: <>Search &ldquo;{input.trim()}&rdquo;</>,
            side: textHint,
            run: () => {
              onText(input.trim());
              reset();
            },
          },
        ],
      });
    }
    return out;
    // `pick` / `onText` are recreated per render; the list depends only on the inputs below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propMode, input, properties, textHint]);

  const flat = groups.flatMap((g) => g.items);
  const showList = open && flat.length > 0;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHl((h) => Math.min(h + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHl((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hl >= 0 && flat[hl]) flat[hl].run();
      else if (!propMode && input.trim()) {
        onText(input.trim());
        reset();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHl(-1);
    } else if (e.key === 'Backspace' && input === '') {
      // Backspace on an empty box takes back the last filter, as in the AWS console.
      if (text) onText(null);
      else {
        const last = [...properties].reverse().find((p) => p.selected.length);
        if (last) onToggle(last.key, last.selected[last.selected.length - 1]);
      }
    }
  };

  const tokens = properties.filter((p) => p.selected.length);
  const anyFilter = tokens.length > 0 || Boolean(text);
  const labelOf = (p: FilterProperty, v: string) => p.options.find((o) => o.value === v)?.label ?? v;

  return (
    <div className="pf" data-testid="property-filter">
      <div className="filter-row pf__row">
        {leading}
        <div className="pf__box">
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={hl >= 0 && flat[hl] ? `${listId}-${hl}` : undefined}
            value={input}
            placeholder={placeholder}
            onChange={(e) => {
              setInput(e.target.value);
              setOpen(true);
              setHl(-1);
            }}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
            data-testid="property-filter-input"
          />
          {showList && (
            <ul id={listId} role="listbox" className="pf__list">
              {groups.map((g) => (
                <li key={g.title} role="presentation">
                  <div className="pf__group">{g.title}</div>
                  <ul role="presentation">
                    {g.items.map((it) => {
                      const i = flat.indexOf(it);
                      return (
                        <li
                          key={it.id}
                          id={`${listId}-${i}`}
                          role="option"
                          aria-selected={i === hl}
                          aria-checked={it.checked}
                          className={`pf__opt${i === hl ? ' pf__opt--active' : ''}`}
                          // mousedown, not click: the input's blur would close the list first.
                          onMouseDown={(e) => {
                            e.preventDefault();
                            it.run();
                          }}
                          onMouseEnter={() => setHl(i)}
                        >
                          {it.checked !== undefined && (
                            <span className="pf__check" aria-hidden="true">
                              {it.checked ? '✓' : ''}
                            </span>
                          )}
                          <span className="pf__label">{it.label}</span>
                          {it.side && <span className="pf__side">{it.side}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
        {trailing}
      </div>

      {anyFilter && (
        <div className="pf__tokens" data-testid="property-filter-tokens">
          {tokens.map((p, i) => (
            <span key={p.key} className="pf__tokwrap">
              {i > 0 && <span className="pf__join">and</span>}
              <span className="pf__token" data-testid={`filter-token-${p.key}`}>
                <span>
                  {p.label} = <b>{p.selected.map((v) => labelOf(p, v)).join(' or ')}</b>
                </span>
                <button type="button" aria-label={`Remove ${p.label} filter`} onClick={() => onClearProperty(p.key)}>
                  ×
                </button>
              </span>
            </span>
          ))}
          {text && (
            <span className="pf__tokwrap">
              {tokens.length > 0 && <span className="pf__join">and</span>}
              <span className="pf__token" data-testid="filter-token-text">
                <span>
                  Text: <b>&ldquo;{text}&rdquo;</b>
                </span>
                <button type="button" aria-label="Remove text search" onClick={() => onText(null)}>
                  ×
                </button>
              </span>
            </span>
          )}
          <button type="button" className="mini-btn" onClick={onClearAll}>
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
